import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Badge, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  IconButton, Link, List, ListItem, ListItemIcon, ListItemText, Snackbar, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import api from "../../api";

const PURPLE = "#7c3aed";
const DONE_GREEN = "#047857";

export const ROADMAP_URL = "admin/analytics/marketing/roadmap";
export const VIEW_STORAGE_KEY = "marketing-roadmap-view";
export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const MAX_FILES = 10;
export const FILE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp", "pdf", "doc", "docx", "xls", "xlsx", "csv", "txt", "ppt", "pptx", "odt", "ods"];
const ACCEPT = FILE_EXTENSIONS.map((e) => `.${e}`).join(",");

// ---------- grouping helpers ----------

export function monthOfWeek(week) {
  return Math.min(6, Math.max(1, Math.ceil((Number(week) || 1) / 4)));
}

export function monthWeeksLabel(month) {
  return month === 6 ? "Weeks 21-26" : `Weeks ${4 * month - 3}-${4 * month}`;
}

export function flattenTasks(phases) {
  return phases.flatMap((p) => p.items.map((item) => ({ ...item, phaseId: p.id, phaseTitle: p.title })));
}

/** Month 1 = weeks 1-4 ... Month 6 = weeks 21-26; tasks sorted by week inside each month. */
export function groupByMonth(phases) {
  const tasks = flattenTasks(phases);
  return [1, 2, 3, 4, 5, 6].map((month) => ({
    month,
    title: `Month ${month}`,
    weeks: monthWeeksLabel(month),
    items: tasks.filter((t) => monthOfWeek(t.week) === month).sort((a, b) => (a.week || 0) - (b.week || 0)),
  }));
}

/** Items of one phase grouped by planned week, in week order. */
export function groupByWeek(items) {
  const groups = [];
  [...items].sort((a, b) => (a.week || 0) - (b.week || 0)).forEach((item) => {
    const last = groups[groups.length - 1];
    if (last && last.week === item.week) last.items.push(item);
    else groups.push({ week: item.week, items: [item] });
  });
  return groups;
}

export function doneCount(items, tasks) {
  return items.filter((i) => tasks?.[i.id]?.completed).length;
}

function readView() {
  try {
    return window.localStorage.getItem(VIEW_STORAGE_KEY) === "month" ? "month" : "week";
  } catch {
    return "week";
  }
}

function asObject(v) {
  return v && !Array.isArray(v) && typeof v === "object" ? v : {};
}

function formatWhen(iso, withTime = false) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const opts = { day: "numeric", month: "short", year: "numeric" };
  if (withTime) Object.assign(opts, { hour: "2-digit", minute: "2-digit" });
  return d.toLocaleString("en-GB", opts);
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function validateFiles(files) {
  if (files.length > MAX_FILES) return `Attach up to ${MAX_FILES} files at a time.`;
  for (const f of files) {
    const ext = String(f.name).split(".").pop().toLowerCase();
    if (!FILE_EXTENSIONS.includes(ext)) return `${f.name}: allowed files are images, PDF, Word, Excel, PowerPoint, CSV or text.`;
    if (f.size > MAX_FILE_BYTES) return `${f.name} is larger than 10 MB.`;
  }
  return "";
}

// ---------- data ----------

/** Shared roadmap progress and comment threads (all admins see the same state). */
export function useRoadmapProgress() {
  const [state, setState] = useState({ loading: true, error: "", tasks: {}, comments: {}, currentUserId: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get(ROADMAP_URL);
        const data = res.data?.data || {};
        if (alive) {
          setState({
            loading: false,
            error: "",
            tasks: asObject(data.tasks),
            comments: asObject(data.comments),
            currentUserId: data.current_user_id ?? null,
          });
        }
      } catch (e) {
        if (alive) {
          setState((s) => ({ ...s, loading: false, error: e?.response?.data?.message || "Progress could not be loaded, so ticking is off for now." }));
        }
      }
    })();
    return () => { alive = false; };
  }, []);

  const setTask = useCallback((key, patch) => {
    const clean = Object.fromEntries(Object.entries(patch || {}).filter(([, v]) => v !== undefined));
    setState((s) => ({ ...s, tasks: { ...s.tasks, [key]: { task_key: key, completed: false, comment_count: 0, ...s.tasks[key], ...clean } } }));
  }, []);

  const toggle = useCallback(async (key, completed) => {
    const res = await api.put(`${ROADMAP_URL}/tasks/${key}`, { completed });
    setTask(key, res.data?.data || { completed });
    return res.data?.data;
  }, [setTask]);

  const addComment = useCallback(async (key, body, files) => {
    const form = new FormData();
    if (body) form.append("body", body);
    files.forEach((f) => form.append("files[]", f));
    const res = await api.post(`${ROADMAP_URL}/tasks/${key}/comments`, form, { headers: { "Content-Type": "multipart/form-data" } });
    const comment = res.data?.data;
    setState((s) => ({ ...s, comments: { ...s.comments, [key]: [...(s.comments[key] || []), comment] } }));
    setTask(key, { comment_count: res.data?.comment_count ?? undefined });
    return comment;
  }, [setTask]);

  const deleteComment = useCallback(async (comment) => {
    const res = await api.delete(`${ROADMAP_URL}/comments/${comment.id}`);
    const key = comment.task_key;
    setState((s) => ({ ...s, comments: { ...s.comments, [key]: (s.comments[key] || []).filter((c) => c.id !== comment.id) } }));
    setTask(key, { comment_count: res.data?.comment_count ?? undefined });
  }, [setTask]);

  return { ...state, toggle, addComment, deleteComment };
}

async function downloadAttachment(attachment) {
  const res = await api.get(attachment.download_url, { responseType: "blob" });
  if (typeof URL.createObjectURL !== "function") return;
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = attachment.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------- pieces ----------

function ImageThumb({ attachment }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url = null;
    let alive = true;
    if (typeof URL.createObjectURL !== "function") return undefined;
    api.get(attachment.download_url, { responseType: "blob" })
      .then((res) => {
        if (!alive) return;
        url = URL.createObjectURL(res.data);
        setSrc(url);
      })
      .catch(() => {});
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [attachment.download_url]);

  if (!src) return null;
  return <Box component="img" src={src} alt={attachment.name} sx={{ display: "block", maxWidth: 160, maxHeight: 110, borderRadius: 1, border: "1px solid #eef0f4", mb: 0.5 }} />;
}

function AttachmentLink({ attachment, onError }) {
  return (
    <Box sx={{ mr: 1.5, mb: 1 }}>
      {attachment.is_image && <ImageThumb attachment={attachment} />}
      <Link
        component="button"
        type="button"
        underline="hover"
        onClick={() => downloadAttachment(attachment).catch(() => onError(`Could not download ${attachment.name}.`))}
        sx={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 0.5 }}
      >
        <InsertDriveFileOutlinedIcon sx={{ fontSize: 15 }} />
        {attachment.name}
      </Link>
      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>{formatSize(attachment.size)}</Typography>
    </Box>
  );
}

function CommentThread({ comments, onDelete, onError }) {
  if (!comments.length) {
    return <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No comments yet.</Typography>;
  }
  return (
    <Box sx={{ mb: 1.5 }} data-testid="roadmap-thread">
      {comments.map((c) => (
        <Box key={c.id} sx={{ py: 1, borderBottom: "1px solid #f1f1f4" }} data-testid={`roadmap-comment-${c.id}`}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{c.author?.name || "Admin"}</Typography>
            <Typography variant="caption" color="text.secondary">{formatWhen(c.created_at, true)}</Typography>
            <Box sx={{ flex: 1 }} />
            {c.can_delete && (
              <Tooltip title="Delete comment">
                <IconButton size="small" aria-label="Delete comment" onClick={() => onDelete(c)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
          {c.body && <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: 0.5 }}>{c.body}</Typography>}
          {c.attachments?.length > 0 && (
            <Stack direction="row" flexWrap="wrap">
              {c.attachments.map((a) => <AttachmentLink key={a.id} attachment={a} onError={onError} />)}
            </Stack>
          )}
        </Box>
      ))}
    </Box>
  );
}

/** Opens after ticking (mode "done", comment optional) or from the comments icon. */
export function TaskDialog({ task, mode, comments, onClose, onSubmit, onDelete, onError }) {
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const open = !!task;

  useEffect(() => {
    if (open) {
      setBody("");
      setFiles([]);
      setError("");
    }
  }, [open, task?.id]);

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const next = [...files, ...picked];
    const problem = validateFiles(next);
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setFiles(next);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() && !files.length) {
      setError("Write a comment or attach a file.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await onSubmit(task, body.trim(), files);
      setBody("");
      setFiles([]);
      if (mode === "done") onClose();
    } catch (err) {
      const errors = err?.response?.data?.errors;
      setError((errors && Object.values(errors).flat()[0]) || err?.response?.data?.message || "Could not save the comment.");
    } finally {
      setSaving(false);
    }
  };

  const done = mode === "done";
  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      {task && (
        <form onSubmit={submit} noValidate>
          <DialogTitle sx={{ pb: 0.5 }}>{done ? "Task marked done" : "Comments"}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>{task.action}</Typography>
            {done && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                Add a note or files about what was done (optional). You can add more later from the comments icon.
              </Typography>
            )}
            <CommentThread comments={comments} onDelete={onDelete} onError={onError} />
            <TextField
              label={done ? "Comment (optional)" : "Add a comment"}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              size="small"
              inputProps={{ maxLength: 5000 }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
              <input ref={inputRef} type="file" multiple accept={ACCEPT} hidden onChange={pickFiles} data-testid="roadmap-file-input" />
              <Button size="small" startIcon={<AttachFileIcon />} onClick={() => inputRef.current?.click()} disabled={saving}>
                Attach files
              </Button>
              <Typography variant="caption" color="text.secondary">Images, PDF, Word, Excel, CSV; up to 10 MB each</Typography>
            </Stack>
            {files.length > 0 && (
              <Stack direction="row" flexWrap="wrap" sx={{ mt: 0.5, gap: 0.5 }}>
                {files.map((f, i) => (
                  <Chip key={`${f.name}-${i}`} size="small" label={`${f.name} (${formatSize(f.size)})`} onDelete={() => setFiles(files.filter((_, j) => j !== i))} />
                ))}
              </Stack>
            )}
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose} disabled={saving}>{done ? "Skip" : "Close"}</Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || (!body.trim() && !files.length)}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
              sx={{ bgcolor: PURPLE, "&:hover": { bgcolor: "#6d28d9" } }}
            >
              {done ? "Save comment" : "Post"}
            </Button>
          </DialogActions>
        </form>
      )}
    </Dialog>
  );
}

function TaskItem({ item, state, disabled, busy, onToggle, onComments, showPhase }) {
  const done = !!state?.completed;
  const count = state?.comment_count || 0;
  return (
    <ListItem alignItems="flex-start" disableGutters sx={{ py: 0.75 }} data-testid={`roadmap-task-${item.id}`}>
      <ListItemIcon sx={{ minWidth: 30, mt: -0.5 }}>
        <Checkbox
          size="small"
          checked={done}
          disabled={disabled || busy}
          onChange={(e) => onToggle(item, e.target.checked)}
          icon={<CheckBoxOutlineBlankIcon fontSize="small" sx={{ color: PURPLE }} />}
          checkedIcon={<CheckBoxIcon fontSize="small" sx={{ color: DONE_GREEN }} />}
          inputProps={{ "aria-label": `Mark done: ${item.action}` }}
          sx={{ p: 0.5, ml: -0.5 }}
        />
      </ListItemIcon>
      <ListItemText
        primary={item.action}
        primaryTypographyProps={{
          variant: "body2",
          sx: { fontWeight: 600, textDecoration: done ? "line-through" : "none", color: done ? "text.secondary" : "text.primary" },
        }}
        secondary={
          <>
            {showPhase && <Box component="span" sx={{ display: "block", color: PURPLE, fontWeight: 600 }}>{item.phaseTitle} · Week {item.week}</Box>}
            <Box component="span" sx={{ display: "block" }}>Copied from: {item.copiedFrom}</Box>
            <Box component="span" sx={{ display: "block" }}>Effort: {item.effort} · Cost: {item.cost}</Box>
            <Box component="span" sx={{ display: "block", color: DONE_GREEN, fontWeight: 600 }}>Target: {item.target}</Box>
            {done && (
              <Box component="span" sx={{ display: "block", color: DONE_GREEN, fontWeight: 700 }} data-testid={`roadmap-done-${item.id}`}>
                Done{state.completed_by?.name ? ` by ${state.completed_by.name}` : ""}{state.completed_at ? ` · ${formatWhen(state.completed_at)}` : ""}
              </Box>
            )}
          </>
        }
        secondaryTypographyProps={{ component: "span", variant: "caption" }}
      />
      <Tooltip title="Comments and files">
        <span>
          <IconButton size="small" onClick={() => onComments(item)} disabled={disabled} aria-label={`Comments (${count}): ${item.action}`} sx={{ mt: -0.25 }}>
            <Badge badgeContent={count} color="secondary" max={99}>
              <ChatBubbleOutlineIcon fontSize="small" />
            </Badge>
          </IconButton>
        </span>
      </Tooltip>
    </ListItem>
  );
}

function ProgressChip({ done, total, testId }) {
  const all = total > 0 && done === total;
  return (
    <Chip
      size="small"
      label={`${done}/${total} done`}
      data-testid={testId}
      sx={{ fontWeight: 700, bgcolor: all ? "#ecfdf5" : "#f5f3ff", color: all ? DONE_GREEN : PURPLE }}
    />
  );
}

// ---------- board ----------

/** The roadmap as a shared to-do list, Week-wise (phase columns) or Month-wise. */
export default function RoadmapBoard({ phases }) {
  const progress = useRoadmapProgress();
  const [view, setView] = useState(readView);
  const [dialog, setDialog] = useState({ task: null, mode: "comments" });
  const [busyKey, setBusyKey] = useState(null);
  const [toast, setToast] = useState(null);

  const allTasks = useMemo(() => flattenTasks(phases), [phases]);
  const months = useMemo(() => groupByMonth(phases), [phases]);
  const disabled = progress.loading || !!progress.error;

  const changeView = (_, next) => {
    if (!next) return;
    setView(next);
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, next); } catch { /* private mode */ }
  };

  const onToggle = async (item, completed) => {
    setBusyKey(item.id);
    try {
      await progress.toggle(item.id, completed);
      if (completed) setDialog({ task: item, mode: "done" });
      else setToast({ severity: "info", text: "Task reopened." });
    } catch (e) {
      setToast({ severity: "error", text: e?.response?.data?.message || "Could not update the task." });
    } finally {
      setBusyKey(null);
    }
  };

  const onDelete = async (comment) => {
    try {
      await progress.deleteComment(comment);
      setToast({ severity: "success", text: "Comment deleted." });
    } catch (e) {
      setToast({ severity: "error", text: e?.response?.data?.message || "Could not delete the comment." });
    }
  };

  const onSubmit = async (task, body, files) => {
    await progress.addComment(task.id, body, files);
    setToast({ severity: "success", text: "Comment added." });
  };

  const renderItem = (item, showPhase = false) => (
    <TaskItem
      key={item.id}
      item={item}
      state={progress.tasks[item.id]}
      disabled={disabled}
      busy={busyKey === item.id}
      onToggle={onToggle}
      onComments={(task) => setDialog({ task, mode: "comments" })}
      showPhase={showPhase}
    />
  );

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 1.5 }}>
        <ToggleButtonGroup size="small" exclusive value={view} onChange={changeView} aria-label="Roadmap view">
          <ToggleButton value="week" sx={{ textTransform: "none", px: 1.5 }}>Week-wise</ToggleButton>
          <ToggleButton value="month" sx={{ textTransform: "none", px: 1.5 }}>Month-wise</ToggleButton>
        </ToggleButtonGroup>
        <ProgressChip done={doneCount(allTasks, progress.tasks)} total={allTasks.length} testId="roadmap-progress-total" />
        {progress.loading && <CircularProgress size={16} />}
        <Typography variant="caption" color="text.secondary">Ticks and comments are shared with every admin.</Typography>
      </Stack>
      {progress.error && <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="roadmap-error">{progress.error}</Alert>}

      {view === "week" ? (
        <Grid container spacing={2}>
          {phases.map((phase) => (
            <Grid item xs={12} lg={4} key={phase.id}>
              <Box sx={{ border: "1px solid #eef0f4", borderRadius: 2, p: 1.5, height: "100%" }} data-testid={`competitors-${phase.id}`}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                  <Typography sx={{ fontWeight: 700, color: PURPLE }}>{phase.title}</Typography>
                  <Chip size="small" label={phase.when} />
                  <ProgressChip done={doneCount(phase.items, progress.tasks)} total={phase.items.length} testId={`roadmap-progress-${phase.id}`} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{phase.goal}</Typography>
                {groupByWeek(phase.items).map((g) => (
                  <Box key={g.week}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.4 }}>
                      Week {g.week}
                    </Typography>
                    <List dense disablePadding>{g.items.map((item) => renderItem(item))}</List>
                  </Box>
                ))}
              </Box>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Grid container spacing={2}>
          {months.map((m) => (
            <Grid item xs={12} md={6} lg={4} key={m.month}>
              <Box sx={{ border: "1px solid #eef0f4", borderRadius: 2, p: 1.5, height: "100%" }} data-testid={`competitors-month-${m.month}`}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }} flexWrap="wrap">
                  <Typography sx={{ fontWeight: 700, color: PURPLE }}>{m.title}</Typography>
                  <Chip size="small" label={m.weeks} />
                  <ProgressChip done={doneCount(m.items, progress.tasks)} total={m.items.length} testId={`roadmap-progress-month-${m.month}`} />
                </Stack>
                {m.items.length ? (
                  <List dense disablePadding>{m.items.map((item) => renderItem(item, true))}</List>
                ) : (
                  <Typography variant="body2" color="text.secondary">No tasks planned this month.</Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      <TaskDialog
        task={dialog.task}
        mode={dialog.mode}
        comments={dialog.task ? progress.comments[dialog.task.id] || [] : []}
        onClose={() => setDialog({ task: null, mode: "comments" })}
        onSubmit={onSubmit}
        onDelete={onDelete}
        onError={(text) => setToast({ severity: "error", text })}
      />
      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? (
          <Alert onClose={() => setToast(null)} severity={toast.severity} variant="filled" sx={{ width: "100%" }} data-testid="roadmap-toast">{toast.text}</Alert>
        ) : <span />}
      </Snackbar>
    </Box>
  );
}
